import React from 'react';
import PropTypes from 'prop-types';
import SheetEdgeHandles from '@components/shared/SheetEdgeHandles';
import SpreadBoxes from '@components/shared/SpreadBoxes';
import HeldPicture from './HeldPicture';
import { FADE_DURATION, getSpreadDescription } from '../imageViewerUtils';
import ImageCaption from './ImageCaption';
import {
    DEFAULT_SPREAD_SETTINGS,
    calculatePagePercentages,
    frontSide,
    getFrameStyle,
    getOffset,
    getPictureHitStyle,
    getPlacement,
    getPlacedImageStyle,
    placementSettings,
    anchorMargins,
} from '../../spreadLayoutUtils';
import usePlacementDrag, { PlacementGrid, PlacementGuides, PlacementHandles } from '../../usePlacementDrag';

/**
 * SpreadSlide Component
 * 
 * Displays two images side-by-side for magazine-style page spreads.
 * Page size is determined by unified page dimensions (calculated from all images).
 * Gap is a percentage of page width.
 * Each image can have its own scale (10-500% or 100-150% for fill) and anchor position within its page.
 * 
 * Uses pure CSS with container queries for reliable sizing:
 * - Outer container has container-type: size
 * - Inner spread uses min(100cqw, 100cqh * aspectRatio) for width
 * - This ensures the spread fits within available space while maintaining aspect ratio
 */
const SpreadSlide = ({ 
    spreadData, 
    alt = '', 
    priority = false, 
    hidden = false,
    opacity,
    isOverlay = false,
    zIndex = 2,
    isMobile = false,
    isLandscape = false,
    style = {},
    gap = 0,
    pageDimensions = null,
    showDescription = false,
    fullWidthOnVertical = false,
    align = 'mc',
    fillScreen = false,
    onUpdateImage,
    onPageClick,
    onResizeSheet,
    onBoxesChange,
}) => {
    const images = spreadData?.images;
    if (!images || images.length === 0) return null;

    const isSingle = images.length === 1;
    const [leftImage, rightImage] = isSingle ? [images[0], null] : images;
    
    // Use unified page dimensions, fallback to default if not provided
    const pageAspectRatio = pageDimensions?.aspectRatio || 2/3;
    
    // Calculate the spread's combined aspect ratio (fixed for all spreads in the gallery)
    // Spread = 2 pages side by side, gap is % of page width
    // spreadWidth = 2 * pageWidth + gap * pageWidth = pageWidth * (2 + gap/100)
    // spreadAspectRatio = spreadWidth / pageHeight = pageAspectRatio * (2 + gap/100)
    const gapFraction = gap / 100;
    const spreadAspectRatio = pageAspectRatio * (2 + gapFraction);
    
    const containerStyle = {
        zIndex,
        opacity: hidden ? 0 : (opacity !== undefined ? opacity : 1),
        transition: opacity !== undefined ? `opacity ${FADE_DURATION}ms ease-in-out` : 'none',
        // Container queries: this container's size is used by children with cqw/cqh units
        containerType: 'size',
        ...style,
    };
    
    // The spread is one imaginary sheet of paper, the same size on every slide: fitted to the
    // viewport and placed at `align` (centred by default), with the images placed inside it.
    // fillScreen: the sheet is the slide itself, whatever its shape.
    // fullWidthOnVertical is for layout hints only; sizing always uses fit formula so it fits screen and maintains ratio.
    const spreadContainerStyle = fillScreen ? { position: 'absolute', inset: 0 } : {
        position: 'absolute',
        inset: 0,
        ...anchorMargins(align),
        width: `min(100cqw, calc(100cqh * ${spreadAspectRatio}))`,
        height: `min(100cqh, calc(100cqw / ${spreadAspectRatio}))`,
    };
    
    const description = isSingle ? (leftImage?.file?.description || '') : getSpreadDescription(spreadData);
    const { pageWidthPercent } = calculatePagePercentages(gap);
    const onTop = frontSide([leftImage, rightImage]);
    // A slide out of view or fading out is only a picture: nothing on it can be pointed at or found.
    const inert = hidden || isOverlay;

    return (
        <div
            className={`absolute inset-0 bg-background ${inert || onUpdateImage ? 'pointer-events-none' : ''}`}
            style={containerStyle}
            inert={inert || undefined}
            aria-hidden={inert || undefined}
        >
            <div
                className={`spread-container isolate flex overflow-hidden ${isSingle ? 'justify-center items-center' : ''}`}
                style={spreadContainerStyle}
            >
                {isSingle ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexShrink: 0 }}>
                        <SpreadImagePanel
                            image={leftImage}
                            alt={alt}
                            priority={priority}
                            pageAspectRatio={pageAspectRatio}
                            gapPercent={0}
                            side="left"
                            fillSpread
                            onUpdate={onUpdateImage}
                            onPageClick={onPageClick}
                        />
                    </div>
                ) : (
                    <>
                        <SpreadImagePanel
                            image={leftImage}
                            alt={`${alt} (left)`}
                            priority={priority}
                            pageAspectRatio={pageAspectRatio}
                            gapPercent={gap}
                            side="left"
                            front={onTop === 0}
                            onUpdate={onUpdateImage}
                            onPageClick={onPageClick}
                        />
                        <SpreadImagePanel
                            image={rightImage}
                            alt={`${alt} (right)`}
                            priority={priority}
                            pageAspectRatio={pageAspectRatio}
                            gapPercent={gap}
                            side="right"
                            front={onTop === 1}
                            onUpdate={onUpdateImage}
                            onPageClick={onPageClick}
                        />
                    </>
                )}
                {(spreadData.boxes?.length > 0 || onBoxesChange) && (
                    <SpreadBoxes
                        boxes={spreadData.boxes}
                        gap={isSingle ? 0 : gap}
                        single={isSingle}
                        onChange={inert ? undefined : onBoxesChange}
                        inert={inert}
                    />
                )}
                {onResizeSheet && !fillScreen && <SheetEdgeHandles align={align} onResize={onResizeSheet} />}
            </div>
            {/* Description - positioned absolutely at bottom for smooth transitions */}
            {showDescription && (
                <div 
                    className="absolute bottom-0 left-0 right-0 pb-4 text-center"
                    style={{
                        opacity: opacity !== undefined ? opacity : 1,
                        transition: opacity !== undefined ? `opacity ${FADE_DURATION}ms ease-in-out` : 'none',
                    }}
                >
                    <ImageCaption
                        description={description}
                        imageId={spreadData?.images?.[0]?.id || spreadData?.id || ''}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Individual image panel within a spread
 * Each panel represents a "page" with unified dimensions. The picture is drawn
 * in a window the size of the page, which the image's offset can move over the
 * other page; the sheet clips whatever leaves it.
 */
function panelPageStyle(gapPercent, side, fillSpread) {
    // Use shared utility for page percentage calculations (ignore when fillSpread)
    const { pageWidthPercent, gapWidthPercent } = calculatePagePercentages(gapPercent);
    // Page container - fillSpread = single image spans full spread (same as admin); otherwise one page in spread
    return {
        position: 'relative',
        width: fillSpread ? '100%' : `${pageWidthPercent}%`,
        height: '100%',
        flexShrink: 0,
        marginRight: !fillSpread && side === 'left' && gapPercent > 0 ? `${gapWidthPercent}%` : undefined,
    };
}

const SpreadImagePanel = ({ image, alt, priority, pageAspectRatio, gapPercent = 0, side, fillSpread = false, front = false, onUpdate, onPageClick }) => {
    if (image?.paper) {
        const outline = onUpdate ? { outline: '1px dashed rgba(128, 128, 128, 0.45)', outlineOffset: -1 } : null;
        return <div data-paper-page className="bg-background" style={{ ...panelPageStyle(gapPercent, side, fillSpread), ...outline }} onClick={onPageClick} />;
    }
    if (!image?.file) return null;
    if (onUpdate) {
        return (
            <EditableSpreadImagePanel
                image={image}
                alt={alt}
                priority={priority}
                pageStyle={panelPageStyle(gapPercent, side, fillSpread)}
                free={!fillSpread}
                front={front}
                onUpdate={onUpdate}
                onPageClick={onPageClick}
            />
        );
    }

    const scale = image.scale || DEFAULT_SPREAD_SETTINGS.scale;
    const fill = image.fill || DEFAULT_SPREAD_SETTINGS.fill;
    const imageStyle = getPlacedImageStyle(scale, fill, getPlacement(image));
    
    return (
        <div style={panelPageStyle(gapPercent, side, fillSpread)}>
            <div style={getFrameStyle(fillSpread ? undefined : getOffset(image), !fillSpread && front, fill)}>
                <HeldPicture
                    file={image.file}
                    alt={alt}
                    priority={priority}
                    style={imageStyle}
                />
            </div>
        </div>
    );
};

/**
 * Editor preview only: drag the picture around (and, in a spread, off) its page
 * and scroll to zoom it. Only the picture takes the pointer, so where two
 * images overlap the one on top is the one that moves.
 */
function EditableSpreadImagePanel({ image, alt, priority, pageStyle, free = false, front = false, onUpdate, onPageClick }) {
    const file = image.file;
    const natural = file?.dimensions?.width
        ? file.dimensions
        : (file?.width ? { width: file.width, height: file.height } : null);
    const { placement, scale, guides, handles, handlers, frameRef } = usePlacementDrag({
        image,
        natural,
        free,
        onPlace: (pos) => onUpdate(image.id, placementSettings(pos)),
        onScale: (size, pos) => onUpdate(image.id, { scale: size, ...placementSettings(pos) }),
    });
    const { ref: pictureRef, style: dragStyle, ...pointer } = handlers;
    const fill = image.fill || DEFAULT_SPREAD_SETTINGS.fill;

    return (
        <div className="select-none" style={pageStyle} onClick={onPageClick}>
            <div
                ref={frameRef}
                style={{ ...getFrameStyle(free ? placement : undefined, free && front, fill), containerType: 'size' }}
            >
                <HeldPicture
                    file={file}
                    alt={alt}
                    priority={priority}
                    style={getPlacedImageStyle(scale, fill, placement)}
                />
                <PlacementGuides guides={guides} grid={!free} />
                <div
                    ref={pictureRef}
                    {...pointer}
                    data-placement-page
                    className="pointer-events-auto"
                    style={{ ...getPictureHitStyle(scale, fill, placement, natural), ...dragStyle }}
                    title={`${free ? 'Drag to position, also onto the other page' : 'Drag to position'}${fill ? '' : ' · drag a corner to resize'} · scroll to zoom`}
                >
                    <PlacementHandles handles={handles} />
                </div>
            </div>
            {free && <PlacementGrid guides={guides} free />}
        </div>
    );
}

EditableSpreadImagePanel.propTypes = {
    image: PropTypes.object.isRequired,
    alt: PropTypes.string,
    priority: PropTypes.bool,
    pageStyle: PropTypes.object.isRequired,
    /** A page of a pair: the image can be moved off it. */
    free: PropTypes.bool,
    /** Drawn over the other page's picture where they overlap, and the one that takes the pointer there. */
    front: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    onPageClick: PropTypes.func,
};

SpreadImagePanel.propTypes = {
    image: PropTypes.shape({
        id: PropTypes.string,
        file: PropTypes.object,
        scale: PropTypes.number,
        anchor: PropTypes.string,
        fill: PropTypes.bool,
        x: PropTypes.number,
        y: PropTypes.number,
        ox: PropTypes.number,
        oy: PropTypes.number,
        front: PropTypes.bool,
    }),
    alt: PropTypes.string,
    priority: PropTypes.bool,
    pageAspectRatio: PropTypes.number,
    gapPercent: PropTypes.number,
    side: PropTypes.oneOf(['left', 'right']),
    fillSpread: PropTypes.bool,
    /** The page's picture is drawn over the other one where they overlap. */
    front: PropTypes.bool,
    onUpdate: PropTypes.func,
    onPageClick: PropTypes.func,
};

SpreadSlide.propTypes = {
    spreadData: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.oneOf(['spread']),
        images: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            file: PropTypes.object,
            scale: PropTypes.number,
            anchor: PropTypes.string,
            fill: PropTypes.bool,
            url: PropTypes.string,
        })).isRequired, // length 1 (single in same container) or 2 (spread)
        /** Blocks placed on the sheet, above the pictures. */
        boxes: PropTypes.array,
    }),
    alt: PropTypes.string,
    priority: PropTypes.bool,
    hidden: PropTypes.bool,
    opacity: PropTypes.number,
    isOverlay: PropTypes.bool,
    zIndex: PropTypes.number,
    isMobile: PropTypes.bool,
    isLandscape: PropTypes.bool,
    style: PropTypes.object,
    gap: PropTypes.number,
    pageDimensions: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        aspectRatio: PropTypes.number,
    }),
    showDescription: PropTypes.bool,
    fullWidthOnVertical: PropTypes.bool,
    /** Where the spread sits in the slide: tl … br (page.slideshow.spread.align). */
    align: PropTypes.oneOf(['tl', 'tc', 'tr', 'ml', 'mc', 'mr', 'bl', 'bc', 'br']),
    /** The spread fills the slide instead of keeping its ratio (page.slideshow.spread.fill). */
    fillScreen: PropTypes.bool,
    /** Editor preview: (imageId, settings) when an image is dragged or zoomed. */
    onUpdateImage: PropTypes.func,
    /** Editor preview: a plain click on an editable page (no drag). */
    onPageClick: PropTypes.func,
    /** Editor preview: (ratio, done) while the sheet's edges are dragged; done with null when cancelled. */
    onResizeSheet: PropTypes.func,
    /** Editor preview: the slide's boxes after one is moved, resized or removed. */
    onBoxesChange: PropTypes.func,
};

export default SpreadSlide;
